import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import type { Creator } from '@/types';

const APPEAL_QKS = [['ai-appeals'], ['ai-limbo'], ['creators'], ['creator']];

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  for (const qk of APPEAL_QKS) qc.invalidateQueries({ queryKey: qk });
}

function nowPb(): string {
  return new Date().toISOString().replace('T', ' ');
}

// Admin acepta una apelación: el lead vuelve a 'validated' y se marca
// appeal_status='accepted' con resolved_at/by. Opcionalmente acepta
// `extraData` para corregir el tipo/categoría/sexo del creator en el
// mismo PATCH (atomic), evitando un round-trip adicional.
export type AcceptAppealArgs = { id: string; extraData?: Partial<Creator> };

export function useAcceptAppeal() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, extraData }: AcceptAppealArgs) =>
      pb.collection('leads').update<Creator>(id, {
        ...(extraData ?? {}),
        appeal_status: 'accepted',
        appeal_resolved_at: nowPb(),
        appeal_resolved_by: user?.id ?? '',
        ai_analysis_status: 'validated',
      }),
    onSuccess: () => {
      invalidateAll(qc);
      toast('Apelación aceptada', 'success');
    },
    onError: () => toast('Error al aceptar apelación', 'error'),
  });
}

// Admin rechaza la apelación vía backend. La ruta marca appeal_status=
// 'rejected' y aplica una sanción REAL al collector (deducción + baneo al
// llegar al límite). Acepta una nota opcional que se guarda en
// appeal_admin_note y se muestra al collector como motivo del rechazo.
export type RejectAppealArgs = { id: string; adminNote?: string };

type RejectAppealResponse = {
  sanctioned: boolean;
  sanctions_count?: number;
  banned?: boolean;
};

function rejectToast(res: RejectAppealResponse): string {
  if (res.banned) return 'Apelación rechazada — usuario baneado';
  if (res.sanctioned) {
    return `Apelación rechazada — sancionado (${res.sanctions_count ?? '?'}/10)`;
  }
  return 'Apelación rechazada';
}

export function useRejectAppeal() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, adminNote }: RejectAppealArgs) =>
      pb.send<RejectAppealResponse>(
        `/api/leads/appeal-reject/${encodeURIComponent(id)}`,
        { method: 'POST', body: { admin_note: (adminNote ?? '').trim() } }
      ),
    onSuccess: (res) => {
      invalidateAll(qc);
      toast(rejectToast(res), 'success');
    },
    onError: () => toast('Error al rechazar apelación', 'error'),
  });
}

// Admin fuerza la validación de un lead que estaba en limbo
// (rejected_by_ai sin apelar). Útil cuando el collector está inactivo
// pero el lead es válido. `extraData` opcional permite corregir
// tipo/categoría/sexo y validar en el mismo PATCH, sin sancionar.
export type ForceValidateArgs = { id: string; extraData?: Partial<Creator> };

export function useForceValidate() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, extraData }: ForceValidateArgs) =>
      pb.collection('leads').update<Creator>(id, {
        ...(extraData ?? {}),
        ai_analysis_status: 'validated',
      }),
    onSuccess: () => {
      invalidateAll(qc);
      toast('Lead validado', 'success');
    },
    onError: () => toast('Error al validar lead', 'error'),
  });
}
